import { Box, CircularProgress, Paper, Typography } from "@mui/material";
import { useContext, useMemo, useState } from "react";
import { EEGMetric, EEGRecording, PairFrequencyBand, SelectedFilterState } from "./types";
import { useRecords } from "../../../api/useRecords";
import { DataFilter, DataFilterContext } from "./components/DataFilter";
import { RecordContext } from "../../../components/record/Record";
import { EEGSelection } from "./components/EEGSelection";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { getFieldsFromParams } from "../../../api/useItemsFromSearchParams";
import { BANDS_IDS } from "./utils/uiUtils";
import { EEGDashboard } from "./components/EEGDashboard";


type EEGAnalysisProps = {
    type: "current" | "comparison";
};

export const EEGAnalysis = ({ type }: EEGAnalysisProps) => {
    const recordContext = useContext(RecordContext);
    const currentEEG = recordContext?.item as EEGRecording;
    const [selectedFilter, setSelectedFilter] = useState<SelectedFilterState>({
        band: [
            String(BANDS_IDS.delta),
            String(BANDS_IDS.theta),
            String(BANDS_IDS.alpha),
            String(BANDS_IDS.beta),
            String(BANDS_IDS.highBeta),
        ],
        individual: [],
    });
    const [params] = useSearchParams();
    const fieldsFromParams = useMemo(() => getFieldsFromParams(params), [params]);


    const { data, status } = useRecords("eeg_metrics", "*.*", {
        extraOptions: { filter: { type: { _in: ["z_score", "zscore_per_hz"] } }, pageSize: -1, sort: "order" },
        staleTime: Infinity,
    });
    const metrics = data?.items as Array<EEGMetric>;

    const { data: bandFrequencyPairs, status: bandFrequencyPairsStatus } = useRecords("pair_frequency_bands", "*.*", {
        staleTime: Infinity,
    });
    const bandFrequencyPairsData = bandFrequencyPairs?.items as Array<PairFrequencyBand>;


    const entityForm = useForm();
    const selectedFirstEEG = entityForm.watch("firstEEG") || currentEEG;
    const selectedSecondEEG = entityForm.watch("secondEEG");

    const hasSameEvaluationSelected = selectedFirstEEG && selectedSecondEEG && selectedFirstEEG === selectedSecondEEG;

    if (status === "loading" || bandFrequencyPairsStatus === "loading") return <CircularProgress />;
    if (status === "error" || bandFrequencyPairsStatus === "error") return <Typography>Error loading data</Typography>;

    return (
        <Box sx={{ mb: 4 }}>
            <DataFilterContext.Provider value={{ selectedFilter, setSelectedFilter }}>
                {type === "comparison" && (
                    <EEGSelection
                        entityForm={entityForm}
                        selectedTreatment={entityForm.watch("treatment")}
                        hasSameEvaluationSelected={hasSameEvaluationSelected}
                        fieldsFromParams={fieldsFromParams}
                    />
                )}
                <Paper sx={{ p: 2, display: "flex", flexDirection: "column", gap: 6, mb: 4 }}>
                    <DataFilter selectedFilter={selectedFilter} setSelectedFilter={setSelectedFilter} />
                </Paper>
                {selectedFirstEEG && metrics && (
                    <EEGDashboard
                        firstEEG={selectedFirstEEG}
                        secondEEG={selectedSecondEEG}
                        metrics={metrics}
                        bandFrequencyPairs={bandFrequencyPairsData}
                    />
                )}
            </DataFilterContext.Provider>

     
        </Box>
    );
};
